/**
 * The loading screen (q-asset-readiness-loading-screen, AD-794): shown from the moment the
 * game view mounts until the readiness session latches (the assets IN VIEW have loaded, or
 * the timeout), so a player — or a devlog capture — never sees a placeholder pill or a prop
 * popping in.
 *
 * It is pure DOM + CSS on purpose (see docs/architecture/rendering-performance.md §
 * "Loading screen"): no second WebGL/WebGPU context (a second context on a phone competes
 * with the very scene it is waiting for, and the real scene must keep rendering underneath
 * so its shaders compile and textures upload before the reveal), identical on both
 * renderers, and no asset fetch of its own — its whole cost is a few KB in the JS/CSS bundle.
 *
 *   - A dawn sky with three drifting cloud banks (CSS gradients, parallax to the pointer).
 *   - The banks PART as the readiness set completes and the world's silhouette sharpens
 *     behind them — the progress picture is the real done / total, never a fake bar.
 *   - A lore card, the world's own words (faction and place descriptions, verbatim from
 *     content/), rotating; click, tap or Space/Enter for the next.
 *   - Reduced motion (OS setting or the Accessibility toggle): no drift, no parallax, an
 *     immediate fade at the reveal.
 */

import { useEffect, useRef, useState, type CSSProperties, type PointerEvent } from 'react';

import { GAME_NAME, TRADE_MARK } from '@great-cloud/shared/client';

import type { ReadinessSession, ReadinessState } from '../../game/readiness.js';
import { LOADING_LORE } from './loadingLore.js';
import {
  LORE_ROTATE_MS,
  REVEAL_MS,
  REVEAL_REDUCED_MS,
  cloudParting,
  describeWaiting,
  nextLoreIndex,
  prefersReducedMotion,
  progressFraction,
} from './loadingModel.js';
import './loading.css';

export interface LoadingScreenProps {
  session: ReadinessSession;
  /** Called once the reveal has finished and the screen should be removed. */
  onRevealed: () => void;
}

export function LoadingScreen({ session, onRevealed }: LoadingScreenProps) {
  const [state, setState] = useState<ReadinessState>(session.state);
  const [lore, setLore] = useState(() => Math.floor(Math.random() * LOADING_LORE.length));
  const [reduced] = useState(() =>
    prefersReducedMotion(
      typeof window !== 'undefined' ? window.matchMedia?.bind(window) : undefined,
      typeof document !== 'undefined' ? document.documentElement.classList : undefined,
    ),
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  const revealedRef = useRef(false);

  useEffect(() => {
    setState(session.state);
    return session.subscribe(setState);
  }, [session]);

  // Rotate the lore while loading.
  useEffect(() => {
    if (state.ready) return;
    const timer = window.setInterval(
      () => setLore((i) => nextLoreIndex(i, LOADING_LORE.length)),
      LORE_ROTATE_MS,
    );
    return () => window.clearInterval(timer);
  }, [state.ready, lore]);

  // The reveal: the banks finish parting, the screen fades, then it is removed.
  useEffect(() => {
    if (!state.ready || revealedRef.current) return;
    const timer = window.setTimeout(
      () => {
        revealedRef.current = true;
        onRevealed();
      },
      reduced ? REVEAL_REDUCED_MS : REVEAL_MS,
    );
    return () => window.clearTimeout(timer);
  }, [state.ready, reduced, onRevealed]);

  const fraction = progressFraction(state.progress);
  const parting = cloudParting(fraction, state.ready);
  const card = LOADING_LORE[lore % LOADING_LORE.length];
  const waiting = describeWaiting(state.waitingOn);
  const style = {
    '--tgc-progress': fraction.toFixed(3),
    '--tgc-parting': parting.toFixed(3),
    '--tgc-reveal-ms': `${reduced ? REVEAL_REDUCED_MS : REVEAL_MS}ms`,
  } as CSSProperties;

  // Parallax look: the pointer leans the banks a little (written straight to CSS vars — no
  // React render per move). Off under reduced motion.
  const onPointerMove = (e: PointerEvent<HTMLDivElement>): void => {
    if (reduced || rootRef.current === null) return;
    const rect = rootRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
    const y = ((e.clientY - rect.top) / Math.max(1, rect.height)) * 2 - 1;
    rootRef.current.style.setProperty('--tgc-look-x', x.toFixed(3));
    rootRef.current.style.setProperty('--tgc-look-y', y.toFixed(3));
  };

  const advance = (): void => setLore((i) => nextLoreIndex(i, LOADING_LORE.length));

  return (
    <div
      ref={rootRef}
      className={[
        'tgc-loading',
        state.ready ? 'is-revealing' : '',
        reduced ? 'is-reduced' : '',
      ].join(' ')}
      style={style}
      onPointerMove={onPointerMove}
      data-testid="loading-screen"
      aria-busy={!state.ready}
    >
      <div className="tgc-loading-sky" aria-hidden="true" />
      <svg
        className="tgc-loading-world"
        viewBox="0 0 1200 400"
        preserveAspectRatio="xMidYMax slice"
        aria-hidden="true"
      >
        <path d="M0 330 Q140 300 260 318 T520 306 T800 322 T1040 300 T1200 316 V400 H0Z" />
        <path
          className="far"
          d="M150 250 q60 -70 130 -40 q40 -50 95 -10 q30 30 -10 52 q-80 26 -190 18 q-55 -6 -25 -20Z"
        />
        <path
          className="far"
          d="M820 214 q50 -60 120 -30 q50 -40 100 6 q20 26 -30 40 q-110 16 -170 4 q-40 -8 -20 -20Z"
        />
        <path d="M470 286 q40 -96 120 -88 q20 -40 70 -26 q60 -30 96 22 q40 6 30 48 q-160 40 -316 44Z" />
      </svg>
      <div className="tgc-loading-bank back" aria-hidden="true" />
      <div className="tgc-loading-bank left" aria-hidden="true" />
      <div className="tgc-loading-bank right" aria-hidden="true" />

      <div className="tgc-loading-panel">
        <h1 className="tgc-loading-title">
          {GAME_NAME}
          <sup className="tgc-tm">{TRADE_MARK}</sup>
        </h1>
        <div className="tgc-loading-status" role="status" aria-live="polite">
          {state.ready ? (
            <span>The clouds part.</span>
          ) : (
            <>
              <span>
                Gathering what is in view: {state.progress.done} of {state.progress.total}
              </span>
              {waiting !== '' ? <span className="tgc-loading-waiting">{waiting}</span> : null}
            </>
          )}
        </div>
        <div className="tgc-loading-meter" aria-hidden="true">
          <div className="tgc-loading-meter-fill" />
        </div>
        {card !== undefined ? (
          <button
            type="button"
            className="tgc-loading-lore"
            onClick={advance}
            aria-label={`${card.title}. ${card.text} Next card.`}
          >
            <span className="tgc-loading-lore-title" key={`t${lore}`}>
              {card.title}
            </span>
            <span className="tgc-loading-lore-text" key={`x${lore}`}>
              {card.text}
            </span>
            <span className="tgc-loading-lore-hint">tap for another</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
